/* ---------- Shared cart state ---------- */

let cartData = null;
let cartPromise = null;

function fetchCartState() {
  cartPromise = fetch(`${Shopify.routes.root}cart.js`)
    .then(r => r.json())
    .then(cart => { cartData = cart; return cart; });
  return cartPromise;
}

function getCart() {
  return cartPromise || fetchCartState();
}

document.addEventListener('cart:refresh', () => fetchCartState().then(broadcastCartState));
document.documentElement.addEventListener('cart:change', (e) => {
  if (e.detail?.cart) {
    const cart = e.detail.cart instanceof Promise ? null : e.detail.cart;
    if (cart) {
      cartData = cart;
      cartPromise = Promise.resolve(cart);
      broadcastCartState(cart);
    } else {
      fetchCartState().then(broadcastCartState);
    }
  }
});

function broadcastCartState(cart) {
  document.dispatchEvent(new CustomEvent('product-card-atc:cart-update', { detail: { cart } }));
}

function uuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/* ---------- Custom element ---------- */

class ProductCardAtc extends HTMLElement {
  connectedCallback() {
    this._variantId = Number(this.dataset.variantId);
    this._isBundle = this.hasAttribute('data-bundle-variant-ids');
    this._bundleVariantIds = this.dataset.bundleVariantIds || '';
    this._bundleParentVariantId = Number(this.dataset.bundleParentVariantId) || this._variantId;
    this._busy = false;

    this._quickBuy = this.previousElementSibling?.matches('.product-card__quick-buy')
      ? this.previousElementSibling
      : this.previousElementSibling?.matches('form[is="product-form"]')
        ? this.previousElementSibling : null;
    this._stepper = this.querySelector('[data-stepper]');
    this._qtyEl = this.querySelector('[data-qty]');
    this._minusBtn = this.querySelector('[data-minus]');
    this._plusBtn = this.querySelector('[data-plus]');

    this._bindEvents();
    this._blockCartRedirect();
    getCart().then(cart => this._syncWithCart(cart));
  }

  _bindEvents() {
    this.addEventListener('click', (e) => e.stopPropagation());

    if (this._isBundle && this._quickBuy) {
      this._quickBuy.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._addBundleToCart();
      });
    }

    this._minusBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this._changeQuantity(-1);
    });
    this._plusBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this._changeQuantity(1);
    });

    this._cartUpdateHandler = (e) => this._syncWithCart(e.detail.cart);
    document.addEventListener('product-card-atc:cart-update', this._cartUpdateHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('product-card-atc:cart-update', this._cartUpdateHandler);
  }

  _blockCartRedirect() {
    const card = this.closest('.product-card') || this.closest('.bundle-step-2__card');
    if (card) {
      card.addEventListener('variant:add', (e) => {
        e.detail.blockCartDrawerOpening = true;
      }, true);
    }
  }

  _getCartItems(cart) {
    if (!cart?.items) return [];
    if (this._isBundle) {
      return cart.items.filter(item =>
        item.variant_id === this._bundleParentVariantId &&
        item.properties?._bundle_variant_ids === this._bundleVariantIds
      );
    }
    return cart.items.filter(item => item.variant_id === this._variantId);
  }

  _getTotalQuantity(cart) {
    const items = this._getCartItems(cart);
    return items.reduce((sum, item) => sum + item.quantity, 0);
  }

  _syncWithCart(cart) {
    const qty = this._getTotalQuantity(cart);
    if (qty > 0) {
      this._showStepper(qty);
    } else {
      this._showAtcButton();
    }
  }

  _showStepper(qty) {
    if (this._quickBuy) this._quickBuy.hidden = true;
    if (this._stepper) this._stepper.hidden = false;
    if (this._qtyEl) this._qtyEl.textContent = qty;
  }

  _showAtcButton() {
    if (this._quickBuy) this._quickBuy.hidden = false;
    if (this._stepper) this._stepper.hidden = true;
  }

  _setBusy(busy) {
    if (!this._quickBuy) return;
    for (const btn of this._quickBuy.querySelectorAll('[is="custom-button"]')) {
      btn.setAttribute('aria-busy', busy ? 'true' : 'false');
    }
  }

  async _addBundleToCart() {
    if (this._busy) return;
    this._busy = true;
    this._setBusy(true);

    try {
      const sectionsToBundle = ['variant-added'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: this._bundleParentVariantId,
            quantity: 1,
            properties: {
              _bundle_uuid: uuid(),
              _bundle_variant_ids: this._bundleVariantIds,
              _bundle_version: new Date().toISOString().split('T')[0] + '_v2'
            }
          }],
          sections: sectionsToBundle.join(','),
          sections_url: `${Shopify.routes.root}variants/${this._bundleParentVariantId}`
        })
      });

      if (!response.ok) throw new Error('Add to cart failed');

      const responseJson = await response.json();
      const cart = await fetchCartState();
      cart.sections = responseJson.sections;

      this.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: {
          items: responseJson.hasOwnProperty('items') ? responseJson.items : [responseJson],
          cart
        }
      }));

      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart }
      }));

    } catch (err) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      this._busy = false;
      this._setBusy(false);
    }
  }

  async _changeQuantity(delta) {
    if (this._busy) return;
    this._busy = true;
    if (this._minusBtn) this._minusBtn.disabled = true;
    if (this._plusBtn) this._plusBtn.disabled = true;

    try {
      const cart = await getCart();
      const items = this._getCartItems(cart);
      if (items.length === 0) return;

      const item = items[0];
      const newQty = Math.max(0, item.quantity + delta);
      await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: newQty })
      });

      if (this.dataset.track === 'accessory') {
        window.LiterboksTracking?.trackAccessorySelected(item, newQty);
      }

      const updatedCart = await fetchCartState();
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'cart:update', cart: updatedCart }
      }));

    } catch (err) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      this._busy = false;
      if (this._minusBtn) this._minusBtn.disabled = false;
      if (this._plusBtn) this._plusBtn.disabled = false;
    }
  }
}

if (!window.customElements.get('product-card-atc')) {
  window.customElements.define('product-card-atc', ProductCardAtc);
}
